import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiUsers,
  FiPrinter,
  FiSearch,
  FiTrendingUp,
  FiDollarSign,
  FiRepeat,
  FiUserPlus,
  FiRefreshCcw,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';
import ClientPagination, { useClientPagination } from '../../components/common/ClientPagination';

const COLORS = ["#7A2238", "#D4AF37", "#5A182A", "#A94D6D", "#2563EB"];

function indiaDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatChartLabel(value) {
  if (!value) return "";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
  }).format(new Date(`${value}T00:00:00.000Z`));
}

function comparisonMeta(value, { inverse = false } = {}) {
  if (value === null || value === undefined) {
    return {
      label: "No comparable data last year",
      className: "bg-gray-100 text-gray-500",
    };
  }

  const percentage = Number(value);
  const positive = inverse ? percentage <= 0 : percentage >= 0;

  return {
    label: `${percentage >= 0 ? "+" : ""}${percentage.toFixed(1)}% vs last year`,
    className: positive
      ? "bg-green-100 text-green-700"
      : "bg-red-100 text-red-700",
  };
}

function EmptyState({ text }) {
  return (
    <div className="flex h-[280px] items-center justify-center text-sm text-gray-400">
      {text}
    </div>
  );
}

export default function CustomerReports() {
  const { accessToken } = useAuth();

  const today = indiaDate();

  const [dateFrom, setDateFrom] = useState(`${today.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [customerType, setCustomerType] = useState("All");
  const [storeId, setStoreId] = useState("");
  const [search, setSearch] = useState("");

  const [dashboard, setDashboard] = useState(null);
  const [stores, setStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadStores = useCallback(async () => {
    if (!accessToken) return;

    try {
      const response = await apiRequest("/stores", { accessToken });
      setStores(response || []);
    } catch {
      setStores([]);
    }
  }, [accessToken]);

  const loadReport = useCallback(
    async (showRefresh = false) => {
      if (!accessToken || !dateFrom || !dateTo) return;

      try {
        if (showRefresh) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }

        setError("");

        const query = new URLSearchParams({
          from: dateFrom,
          to: dateTo,
        });

        if (storeId) query.set("storeId", storeId);

        if (customerType !== "All") {
          query.set("customerType", customerType.toUpperCase());
        }

        const response = await apiRequest(
          `/reports/customers?${query.toString()}`,
          { accessToken },
        );

        setDashboard(response);
      } catch (requestError) {
        setError(requestError.message || "Unable to load customer report.");
        setDashboard(null);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [accessToken, dateFrom, dateTo, storeId, customerType],
  );

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const metrics = dashboard?.metrics || {};
  const changes = dashboard?.comparison?.changes || {};

  const growthTrend = dashboard?.growthTrend || [];
  const previousGrowthTrend = dashboard?.previousGrowthTrend || [];

  const comparisonTrend = useMemo(() => {
    const previousByIndex = new Map(
      previousGrowthTrend.map((item, index) => [index, item]),
    );

    return growthTrend.map((item, index) => ({
      label: formatChartLabel(item.date),
      thisYear: Number(item.customers || 0),
      lastYear: Number(previousByIndex.get(index)?.customers || 0),
    }));
  }, [growthTrend, previousGrowthTrend]);

  const customers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return (dashboard?.customers || []).filter((customer) => {
      if (!normalizedSearch) return true;

      return (
        customer.name?.toLowerCase().includes(normalizedSearch) ||
        customer.phone?.toLowerCase().includes(normalizedSearch) ||
        customer.email?.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [dashboard?.customers, search]);

  const customerTypes = dashboard?.customerTypes || [];
  const topCustomers = dashboard?.topCustomers || [];

  const cards = [
    {
      title: "Total Customers",
      value: formatNumber(metrics.totalCustomers),
      comparison: comparisonMeta(changes.totalCustomers),
      icon: <FiUsers size={24} />,
      color: "bg-blue-50 text-blue-600",
    },
    {
      title: "New Customers",
      value: formatNumber(metrics.newCustomers),
      comparison: comparisonMeta(changes.newCustomers),
      icon: <FiUserPlus size={24} />,
      color: "bg-green-50 text-green-600",
    },
    {
      title: "Repeat Customers",
      value: formatNumber(metrics.repeatCustomers),
      comparison: comparisonMeta(changes.repeatCustomers),
      icon: <FiRepeat size={24} />,
      color: "bg-purple-50 text-purple-600",
    },
    {
      title: "Average Order",
      value: formatMoney(metrics.averageOrder),
      comparison: comparisonMeta(changes.averageOrder),
      icon: <FiTrendingUp size={24} />,
      color: "bg-yellow-50 text-yellow-700",
    },
    {
      title: "Customer Revenue",
      value: formatMoney(metrics.revenue),
      comparison: comparisonMeta(changes.revenue),
      icon: <FiDollarSign size={24} />,
      color: "bg-pink-50 text-pink-600",
    },
    {
      title: "Outstanding",
      value: formatMoney(metrics.outstanding),
      comparison: comparisonMeta(changes.outstanding, { inverse: true }),
      icon: <FiDollarSign size={24} />,
      color: "bg-red-50 text-red-600",
    },
  ];


  const pagination = useClientPagination(customers);
  useEffect(() => { pagination.setPage(1); }, [search]);

  return (
    <div className="charms-analytics space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary-800">
            Customer Analytics
          </h1>

          <p className="mt-2 text-gray-500">
            Customer sales, loyalty, purchase behaviour, and last-year comparison.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => window.print()}
            className="rounded-xl border bg-white px-5 py-3"
            title="Print report"
          >
            <FiPrinter />
          </button>

          <button
            onClick={() => loadReport(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 rounded-xl border bg-white px-5 py-3 disabled:opacity-50"
          >
            <FiRefreshCcw className={isRefreshing ? "animate-spin" : ""} />
            Refresh
          </button>

        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(event) => setDateFrom(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          />

          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            max={today}
            onChange={(event) => setDateTo(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          />

          <select
            value={customerType}
            onChange={(event) => setCustomerType(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="All">All Customers</option>
            <option value="Retail">Retail</option>
            <option value="Wholesale">Wholesale</option>
          </select>

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="">All Stores</option>

            {stores
              .filter((store) => store.isActive)
              .map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name} ({store.code})
                </option>
              ))}
          </select>

          <button
            onClick={() => loadReport(true)}
            className="rounded-xl bg-primary-800 px-5 py-3 font-medium text-white"
          >
            Generate Report
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-3xl bg-white py-24 text-center text-gray-500 shadow-sm">
          Loading customer report...
        </div>
      ) : dashboard ? (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((card) => (
              <div key={card.title} className="analytics-kpi">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{card.title}</p>

                    <h3 className="mt-2 text-3xl font-bold">{card.value}</h3>

                    <span
                      className={`mt-3 inline-block rounded-full px-3 py-1 text-xs font-semibold ${card.comparison.className}`}
                    >
                      {card.comparison.label}
                    </span>
                  </div>

                  <div className="analytics-icon flex items-center justify-center">
                    {card.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-2 text-xl font-bold">Customer Growth Comparison</h2>

              <p className="mb-5 text-sm text-gray-500">
                New customer registrations: selected period vs last year.
              </p>

              {comparisonTrend.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={comparisonTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip />
                    <Legend />

                    <Line
                      type="monotone"
                      dataKey="thisYear"
                      name="This Year"
                      stroke="#7A2238"
                      strokeWidth={3}
                    />

                    <Line
                      type="monotone"
                      dataKey="lastYear"
                      name="Last Year"
                      stroke="#D4AF37"
                      strokeWidth={3}
                      strokeDasharray="7 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No customer growth data for this period." />
              )}
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-2 text-xl font-bold">Customer Type</h2>

              <p className="mb-5 text-sm text-gray-500">
                Sales contribution by customer segment.
              </p>

              {customerTypes.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={customerTypes}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={110}
                    >
                      {customerTypes.map((item, index) => (
                        <Cell
                          key={`${item.name}-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No customer segment data available." />
              )}
            </section>
          </div>

          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold">Customer Report</h2>
                <p className="text-gray-500">Customer-wise sales summary</p>
              </div>

              <div className="relative w-full lg:w-80">
                <FiSearch className="absolute left-4 top-4 text-gray-400" />

                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search customer..."
                  className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-[#F8F6F5]">
                  <tr>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-center">Orders</th>
                    <th className="px-4 py-3 text-center">Qty</th>
                    <th className="px-4 py-3 text-right">Sales</th>
                    <th className="px-4 py-3 text-right">Discount</th>
                    <th className="px-4 py-3 text-right">Avg. Order</th>
                    <th className="px-4 py-3 text-center">Loyalty</th>
                    <th className="px-4 py-3 text-right">Outstanding</th>
                    <th className="px-4 py-3">Last Purchase</th>
                  </tr>
                </thead>

                <tbody>
                  {customers.length ? (
                    pagination.pageItems.map((customer) => (
                      <tr key={customer.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <p className="font-semibold">{customer.name}</p>
                          <p className="text-sm text-gray-500">
                            {customer.phone || customer.email || "—"}
                          </p>
                        </td>

                        <td className="px-4 py-4 text-center">
                          {formatNumber(customer.orders)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          {formatNumber(customer.quantity)}
                        </td>

                        <td className="px-4 py-4 text-right font-semibold">
                          {formatMoney(customer.sales)}
                        </td>

                        <td className="px-4 py-4 text-right">
                          {formatMoney(customer.discount)}
                        </td>

                        <td className="px-4 py-4 text-right">
                          {formatMoney(customer.averageOrder)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          {formatNumber(customer.loyaltyPoints)}
                        </td>

                        <td className="px-4 py-4 text-right font-semibold text-red-600">
                          {formatMoney(customer.outstanding)}
                        </td>

                        <td className="px-4 py-4">
                          {formatDate(customer.lastPurchaseAt)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="py-16 text-center text-gray-400">
                        No customers found for this selection.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <ClientPagination pagination={pagination} label="customers" />
            </div>
          </section>

          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold">Top 5 Customers</h2>
              <span className="text-sm text-gray-400">By selected-period sales</span>
            </div>

            <div className="space-y-4">
              {topCustomers.length ? (
                topCustomers.map((customer, index) => (
                  <div
                    key={customer.id || customer.name}
                    className="flex items-center justify-between rounded-2xl border p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-800 text-lg font-bold text-white">
                        {index + 1}
                      </div>

                      <div>
                        <p className="font-semibold">{customer.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatNumber(customer.orders)} Orders
                        </p>
                      </div>
                    </div>

                    <p className="text-xl font-bold text-green-600">
                      {formatMoney(customer.sales)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="py-10 text-center text-sm text-gray-400">
                  No customer sales found.
                </p>
              )}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
